import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { Spin, Tag, Tooltip } from "antd";
import { useSearchParams } from "react-router-dom";

import SearchFilter from "../../../components/Filters/SearchFilter";
import TranslatedStatus from "../../../components/TranslatedStatus";
import NodeV5ChannelListActionButtons from "./NodeV5ChannelListActionButtons";
import NodeChannelsListBitrate from "./NodeChannelsListBitrate";
import SourceSwitch from "../../../components/SourceSwitch";
import ClientsConnected from "../../../components/NodeChannelFormV5/DetailSection/ClientsConnected";
import EnginePlayoutSourceSwitch from "../../../components/EnginePlayoutSourceSwitch";
import getActiveInputType from "../../../lib/utils/getActiveInputType";

import { ENGINE_TYPES, INPUT_STATUS_FILTERS, NODE_CHANNEL_STATE } from "../../../lib/utils/constants";
import { compareBitrateColumn } from "../../../lib/utils/nodeChannelsListBitrateSort";
import { compareInputSourceColumn } from "../../../lib/utils/nodeChannelsListInputSourceSort";

const DESCRIPTION_PREVIEW_LENGTH = 8;

const translations = defineMessages({
  name: { id: "NodeChannelsListColumns.name", defaultMessage: "Name" },
  description: {
    id: "NodeChannelsListColumns.description",
    defaultMessage: "Description",
  },
  type: { id: "NodeChannelsListColumns.type", defaultMessage: "Type" },
  logs: {
    id: "NodeChannelsListColumns.logs",
    defaultMessage: "Logs",
  },
  stats: {
    id: "NodeChannelsListColumns.stats",
    defaultMessage: "Stats",
  },
  mainInput: {
    id: "NodeChannelsListColumns.mainInput",
    defaultMessage: "Main",
  },
  backupInput: {
    id: "NodeChannelsListColumns.backupInput",
    defaultMessage: "Backup",
  },
  inputSource: {
    id: "NodeChannelsListColumns.inputSource",
    defaultMessage: "Input source",
  },
  channelStatus: {
    id: "NodeChannelsListColumns.channelStatus",
    defaultMessage: "Channel status",
  },
  licenseStatus: {
    id: "NodeChannelsListColumns.licenseStatus",
    defaultMessage: "License status",
  },
  actions: {
    id: "NodeChannelsListColumns.actions",
    defaultMessage: "Actions",
  },
  inputBitrate: {
    id: "NodeChannelsListColumns.inputBitrate",
    defaultMessage: "Input bitrate",
  },
  connected: {
    id: "NodeChannelsListColumns.connected",
    defaultMessage: "Connected",
  },
  waitingForStart: {
    id: "NodeChannelsListColumns.waitingForStart",
    defaultMessage: "Waiting for start",
  },
});

function ChannelDescriptionCell({ description }) {
  const text = description != null && description !== "" ? String(description) : "";

  if (!text) {
    return null;
  }

  const preview = text.length > DESCRIPTION_PREVIEW_LENGTH ? `${text.slice(0, DESCRIPTION_PREVIEW_LENGTH)}…` : text;

  return (
    <Tooltip title={text}>
      <span>{preview}</span>
    </Tooltip>
  );
}

const NodeChannelsListColumns = ({ cwid, cnn, showDescriptionColumn = false }) => {
  const { formatMessage } = useIntl();
  const [searchParams] = useSearchParams();
  const inputStatus = searchParams.get("inputStatus");
  const search = searchParams.get("search");
  const channelDescriptionSearch = searchParams.get("channelDescription");
  const engineTypeParam = searchParams.get("engineType");

  const parsedInputStatus = inputStatus && inputStatus.length > 0 ? inputStatus.split(",").filter(Boolean) : null;
  const parsedEngineTypes =
    engineTypeParam && engineTypeParam.length > 0 ? engineTypeParam.split(",").filter(Boolean) : null;

  const descriptionColumn = {
    key: "description",
    title: formatMessage(translations.description),
    ...SearchFilter({
      cwid,
      cnn,
      dataIndex: "channelDescription",
      placeholder: "description",
      searchParamKey: "channelDescription",
    }),
    ...(channelDescriptionSearch ? { filteredValue: [channelDescriptionSearch] } : { filteredValue: null }),
    render: (_, record) => <ChannelDescriptionCell description={record?.config?.description} />,
    sorter: (a, b) =>
      (a?.config?.description ?? "").localeCompare(b?.config?.description ?? "", undefined, {
        sensitivity: "base",
      }),
  };

  const columns = [
    {
      key: "name",
      title: formatMessage(translations.name),
      ...SearchFilter({ cwid, cnn, dataIndex: "searchAll", placeholder: "all", hidden: true }),
      ...(search ? { filteredValue: [search] } : { filteredValue: null }),
      className: "searchAllFilter",
      sorter: (currentValue, nextValue) => currentValue.config.name.localeCompare(nextValue.config.name),
    },
    ...(showDescriptionColumn ? [descriptionColumn] : []),
    {
      key: "config",
      title: formatMessage(translations.type),
      align: "center",
      filters: [
        {
          text: "Transcoding",
          value: ENGINE_TYPES.TRANSCODING,
        },
        {
          text: "Playout",
          value: ENGINE_TYPES.PLAYOUT,
        },
        {
          text: "Direct",
          value: ENGINE_TYPES.DIRECT,
        },
      ],
      onFilter: (value, record) => record.config?.type === value,
      ...(parsedEngineTypes?.length ? { filteredValue: parsedEngineTypes } : { filteredValue: null }),
      render: ({ config: { type } }) => {
        return <Tag color="default">{type && type.toUpperCase()}</Tag>;
      },
      sorter: (currentValue, nextValue) => currentValue.config.type.localeCompare(nextValue.config.type),
    },
    {
      key: "status",
      align: "center",
      title: formatMessage(translations.channelStatus),
      dataIndex: "status",
      filters: INPUT_STATUS_FILTERS,
      filterSearch: true,
      onFilter: (value, record) => (record.status?.channelStatusText ?? "").indexOf(String(value)) === 0,
      ...(parsedInputStatus?.length ? { filteredValue: parsedInputStatus } : { filteredValue: null }),
      render: (status) => {
        if (status?.channelStatusText === NODE_CHANNEL_STATE.PROCESS_DISCONNECTED) {
          return <Spin size="small" />;
        }

        return status?.channelStatusText ? <TranslatedStatus channelStatus={status.channelStatusText} /> : null;
      },
      sorter: (currentValue, nextValue) =>
        currentValue.status.channelStatusText.localeCompare(nextValue.status.channelStatusText),
    },
    {
      key: "InputSource",
      align: "center",
      title: formatMessage(translations.inputSource),
      dataIndex: "status",
      width: 250,
      sorter: (a, b) => compareInputSourceColumn(a, b),
      render: (status, { channelId, config: { type } }) => {
        const { playingSource, requestedStatusText, inputs: inputsStatus } = status;
        const isEnginePlayout = type === ENGINE_TYPES.PLAYOUT;
        if (isEnginePlayout) {
          return (
            <EnginePlayoutSourceSwitch
              channelId={channelId}
              requestedStatusText={requestedStatusText}
              inputsStatus={inputsStatus}
            />
          );
        }

        if (inputsStatus && inputsStatus.length === 0) {
          return <span>N/A</span>;
        }

        return (
          <SourceSwitch
            channelId={channelId}
            playingSource={playingSource}
            requestedStatusText={requestedStatusText}
            inputsStatus={inputsStatus}
            type={type}
          />
        );
      },
    },
    {
      key: "inputBitrate",
      align: "center",
      title: formatMessage(translations.inputBitrate),
      dataIndex: "status",
      sorter: (a, b) => compareBitrateColumn(a, b),
      render: (status, record) => {
        const inputType = getActiveInputType({
          playingSource: record?.status?.playingSource,
          config: record?.config,
        });

        return (
          <NodeChannelsListBitrate
            channelId={status?.channelId}
            channelStatus={status?.channelStatusText}
            inputType={inputType}
            playingSource={status?.playingSource}
          />
        );
      },
    },
    {
      key: "connected",
      align: "center",
      title: formatMessage(translations.connected),
      dataIndex: "status",
      sorter: (a, b) => Number(a?.status?.connected ?? 0) - Number(b?.status?.connected ?? 0),
      render: ({ connected, outputs }) => {
        return <ClientsConnected connected={connected} outputs={outputs} />;
      },
    },
    {
      key: "actions",
      fixed: "right",
      width: 400,
      title: formatMessage(translations.actions),
      render: (text, { status, channelId, config }) => {
        return <NodeV5ChannelListActionButtons channelConfig={config} channelId={channelId} status={status} />;
      },
    },
  ];

  return columns;
};

export default NodeChannelsListColumns;
